import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { FileText, Download, Copy, ArrowLeft, ArrowRight, PackageOpen, Save, FolderPlus, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { ReadAloud } from "@/components/ReadAloud";

const KITS = [
  { id: "trust", title: "Form a Trust — Guided Packet", filename: "trust-formation-packet",
    blurb: "Step through the full trust structure in order and download every document together as one packet.",
    slugs: ["declaration-of-trust", "trust-registry", "trust-certificate-units",
      "security-agreement", "hold-harmless-indemnity", "power-of-attorney", "bill-of-exchange"] },
  { id: "litigation", title: "Prepare to File — Litigation Packet", filename: "prepare-to-file-packet",
    blurb: "Bundle the Notice of Intent to Sue, Civil Complaint, and First Set of Discovery Demands into one Level 2 & 3 packet — shared fields carry across every document.",
    slugs: ["notice-of-intent-to-sue", "civil-complaint", "first-discovery-set"] },
];

function renderBody(body, values) {
  return body.replace(/\{\{(\w+)\}\}/g, (_, k) => (values[k]?.trim() ? values[k] : `_______`));
}

function Fields({ fields, values, setValues }) {
  return fields.map((f) => (
    <div key={f.name}>
      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">{f.label}</label>
      {f.type === "textarea" ? (
        <textarea data-testid={`field-${f.name}`} rows={3} value={values[f.name] || ""}
          onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
          className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm" />
      ) : (
        <input data-testid={`field-${f.name}`} value={values[f.name] || ""}
          onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
          className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm" />
      )}
    </div>
  ));
}

export default function Templates() {
  const location = useLocation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [values, setValues] = useState({});
  const [folders, setFolders] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveFolder, setSaveFolder] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [editingDoc, setEditingDoc] = useState(null);
  // Kit mode
  const [kit, setKit] = useState(null);       // array of full templates
  const [kitStep, setKitStep] = useState(0);
  const [kitValues, setKitValues] = useState({});
  const [kitMeta, setKitMeta] = useState(null);

  const loadFolders = () => api.get("/folders").then((r) => setFolders(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    loadFolders();
  }, []);

  // Re-open a saved document for editing (from Research)
  useEffect(() => {
    const doc = location.state?.doc;
    if (doc && doc.template_slug) {
      api.get(`/templates/${doc.template_slug}`).then(({ data }) => {
        setActive(data);
        setValues(doc.values || {});
        setEditingDoc(doc.id);
        setSaveName(doc.title || data.title);
        setSaveFolder(doc.folder_id || "");
      });
      navigate("/templates", { replace: true, state: {} });
    }
    // eslint-disable-next-line
  }, [location.state]);

  const open = async (slug) => {
    const { data } = await api.get(`/templates/${slug}`);
    setActive(data); setValues({}); setEditingDoc(null); setSaveName(data.title); setSaveFolder("");
  };

  const backToList = () => { setActive(null); setKit(null); setShowSave(false); setEditingDoc(null); };

  const filled = active ? renderBody(active.body, values) : "";

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Could not copy — select and copy manually"); }
  };
  const downloadTxt = (text, name) => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const doSave = async () => {
    const folder_id = saveFolder || null;
    const payload = { kind: "document", title: saveName || active.title, template_slug: active.slug, values, content: filled, folder_id };
    try {
      if (editingDoc) {
        await api.patch(`/bookmarks/${editingDoc}`, { title: payload.title, values, content: filled, folder_id });
        toast.success("Document updated in Research");
      } else {
        await api.post("/bookmarks", payload);
        toast.success("Saved to Research");
      }
      setShowSave(false);
    } catch { toast.error("Could not save"); }
  };
  const createFolderInline = async () => {
    const name = newFolder.trim();
    if (!name) return;
    const { data } = await api.post("/folders", { name });
    setNewFolder(""); await loadFolders(); setSaveFolder(data.id);
    toast.success(`Folder “${name}” created`);
  };

  // ---------- Guided Kits ----------
  const startKit = async (cfg) => {
    const docs = await Promise.all(cfg.slugs.map((s) => api.get(`/templates/${s}`).then((r) => r.data)));
    setKit(docs); setKitMeta(cfg); setKitStep(0); setKitValues({}); setActive(null);
  };
  const kitDocs = () => (kit || []).map((t) => ({ title: t.title, body: renderBody(t.body, kitValues) }));
  const downloadKit = async (format) => {
    try {
      const res = await api.post("/templates/kit/download",
        { format, documents: kitDocs(), title: kitMeta?.title || "Document Packet" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${kitMeta?.filename || "packet"}.${format}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded packet (.${format})`);
    } catch { toast.error("Could not build the packet"); }
  };

  // ================= KIT WIZARD =================
  if (kit) {
    const t = kit[kitStep];
    const preview = renderBody(t.body, kitValues);
    const last = kitStep === kit.length - 1;
    return (
      <PageShell>
        <div className="max-w-7xl mx-auto px-6 py-14">
          <button onClick={backToList} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="w-4 h-4" /> All templates</button>
          <div className="flex items-center gap-3 mb-2">
            <PackageOpen className="w-7 h-7 text-primary" />
            <h1 className="font-serif text-4xl">{kitMeta?.title || "Guided Packet"}</h1>
          </div>
          <p className="text-muted-foreground mb-6 max-w-2xl">Fill each document in order — shared details like the trust name carry across. At the end, download the whole packet together. Educational study only — not legal advice.</p>

          <div className="flex flex-wrap gap-2 mb-8" data-testid="kit-steps">
            {kit.map((k, i) => (
              <button key={k.slug} onClick={() => setKitStep(i)} data-testid={`kit-step-${i}`}
                className={`px-3 py-1.5 text-xs font-mono border transition-colors ${i === kitStep ? "bg-primary text-primary-foreground border-primary" : i < kitStep ? "border-primary/40 text-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                {i + 1}. {k.title.replace(/\s*\(Study Template\)/, "")}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-px bg-border border border-border">
            <div className="bg-card p-8 space-y-5">
              <span className="text-xs uppercase tracking-widest font-mono text-primary">Step {kitStep + 1} of {kit.length} · {t.title}</span>
              <Fields fields={t.fields} values={kitValues} setValues={setKitValues} />
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setKitStep((s) => Math.max(0, s - 1))} disabled={kitStep === 0} data-testid="kit-prev"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Back</button>
                {!last ? (
                  <button onClick={() => setKitStep((s) => Math.min(kit.length - 1, s + 1))} data-testid="kit-next"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">Next document <ArrowRight className="w-4 h-4" /></button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => downloadKit("txt")} data-testid="kit-download-txt"
                      className="inline-flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm text-sm hover:border-primary hover:text-primary"><Download className="w-4 h-4" /> .txt</button>
                    <button onClick={() => downloadKit("docx")} data-testid="kit-download-docx"
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform"><Download className="w-4 h-4" /> Download packet (.docx)</button>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-card p-8">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest font-mono text-primary">Live preview</span>
                <ReadAloud label="Read aloud" getText={() => preview} />
              </div>
              <pre className="mt-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground/85 bg-background border border-border p-6 rounded-sm min-h-[400px]" data-testid="kit-preview">{preview}</pre>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ================= SINGLE TEMPLATE / LIST =================
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-14">
        {!active ? (
          <>
            <span className="text-xs uppercase tracking-widest font-mono text-primary">Documents</span>
            <h1 className="font-serif text-5xl mt-2 mb-3">Fillable Templates</h1>
            <p className="text-muted-foreground max-w-2xl mb-10">Study templates you can fill, preview live, then copy, download, or save to your Research folders to edit later. For educational study only — not legal advice.</p>

            {KITS.map((cfg) => (
              <button key={cfg.id} onClick={() => startKit(cfg)} data-testid={`start-kit-${cfg.id}`}
                className="w-full mb-4 bg-gradient-to-r from-primary/15 to-transparent border border-primary/40 p-7 text-left group hover:from-primary/25 transition-colors flex items-center gap-5">
                <PackageOpen className="w-10 h-10 text-primary shrink-0" />
                <div className="flex-1">
                  <h2 className="font-serif text-2xl leading-tight">{cfg.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{cfg.blurb}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
            <div className="mb-8" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
              {templates.map((t) => (
                <button key={t.slug} onClick={() => open(t.slug)} data-testid={`template-${t.slug}`} className="bg-card p-7 text-left group hover:bg-muted transition-colors">
                  <FileText className="w-7 h-7 text-primary mb-6" />
                  <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{t.category}</span>
                  <h2 className="font-serif text-xl mt-1 leading-tight">{t.title}</h2>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t.description}</p>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button onClick={backToList} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> All templates
            </button>
            <h1 className="font-serif text-4xl mb-8">{active.title}{editingDoc && <span className="text-base font-mono text-primary ml-3">· editing saved document</span>}</h1>

            <div className="grid lg:grid-cols-2 gap-px bg-border border border-border">
              <div className="bg-card p-8 space-y-5">
                <span className="text-xs uppercase tracking-widest font-mono text-primary">Fill the fields</span>
                <Fields fields={active.fields} values={values} setValues={setValues} />
                <div className="flex flex-wrap gap-3 pt-2">
                  <button onClick={() => copy(filled)} data-testid="copy-template" className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm text-sm hover:border-primary hover:text-primary transition-colors">
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                  <button onClick={() => downloadTxt(filled, `${active.slug}.txt`)} data-testid="download-template" className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm text-sm hover:border-primary hover:text-primary transition-colors">
                    <Download className="w-4 h-4" /> .txt
                  </button>
                  <button onClick={() => setShowSave((v) => !v)} data-testid="save-doc-toggle" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
                    <Save className="w-4 h-4" /> {editingDoc ? "Update in Research" : "Save to Research"}
                  </button>
                </div>

                {showSave && (
                  <div className="border border-border rounded-sm p-4 bg-muted/30 space-y-3" data-testid="save-doc-panel">
                    <div>
                      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Document name</label>
                      <input value={saveName} onChange={(e) => setSaveName(e.target.value)} data-testid="save-doc-name"
                        className="w-full bg-background border border-input px-3 py-2 rounded-sm text-sm outline-none focus:border-primary" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Folder</label>
                      <div className="flex gap-2">
                        <select value={saveFolder} onChange={(e) => setSaveFolder(e.target.value)} data-testid="save-doc-folder"
                          className="flex-1 bg-background border border-input px-3 py-2 rounded-sm text-sm outline-none focus:border-primary">
                          <option value="">Unfiled</option>
                          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="New folder…" data-testid="save-doc-newfolder"
                          className="flex-1 bg-background border border-input px-3 py-2 rounded-sm text-sm outline-none focus:border-primary" />
                        <button onClick={createFolderInline} data-testid="save-doc-newfolder-create" className="flex items-center gap-1.5 border border-border px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-mono hover:border-primary hover:text-primary"><FolderPlus className="w-4 h-4" /> Add</button>
                      </div>
                    </div>
                    <button onClick={doSave} data-testid="save-doc-confirm" className="w-full bg-primary text-primary-foreground py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
                      {editingDoc ? "Update document" : "Save document"}
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-card p-8">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest font-mono text-primary">Live preview</span>
                  <ReadAloud label="Read aloud" getText={() => filled} />
                </div>
                <pre className="mt-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground/85 bg-background border border-border p-6 rounded-sm min-h-[400px]" data-testid="template-preview">
                  {filled}
                </pre>
              </div>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}
